/**
 * QuestionCard Component
 *
 * Displays a single questionnaire question with:
 * - Question text and number
 * - Example/help text
 * - Rating scale (1-5)
 * - Optional context notes field
 * - Visual feedback for answered state
 */

import React, { useState, useEffect } from "react";

import { RATING_SCALE, RATING_LABELS } from "../constants";
import type { QuestionnaireQuestion } from "../questionnaire.service";

interface QuestionCardProps {
  question: QuestionnaireQuestion;
  value?: number;
  contextNotes?: string;
  onChange: (value: number, notes?: string, saveImmediately?: boolean) => void;
  required?: boolean;
  showExample?: boolean;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  value,
  contextNotes = "",
  onChange,
  required = true,
  showExample = true,
}) => {
  const [showNotes, setShowNotes] = useState(!!contextNotes);
  const [localNotes, setLocalNotes] = useState(contextNotes);

  // Update localNotes and showNotes when contextNotes prop changes (e.g., on page reload)
  useEffect(() => {
    setLocalNotes(contextNotes);
    if (contextNotes) {
      setShowNotes(true);
    }
  }, [contextNotes]);

  const handleRatingChange = (newValue: number) => {
    // Trigger immediate save when rating is selected
    onChange(newValue, localNotes, true);
  };

  const handleNotesChange = (notes: string) => {
    setLocalNotes(notes);
    if (value) {
      // No immediate save for notes - let auto-save handle it
      onChange(value, notes, false);
    }
  };

  const isAnswered = value !== undefined;

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border-2 p-3 sm:p-6 transition-all ${
        isAnswered
          ? "border-green-200 bg-green-50/30"
          : "border-gray-200 hover:border-blue-300"
      }`}
    >
      {/* Question Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <span className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-blue-100 text-blue-700 font-semibold text-xs sm:text-sm flex-shrink-0">
              {question.question_number}
            </span>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              {question.question_text}
              {required && <span className="text-red-500 ml-1">*</span>}
            </h3>
          </div>

          {/* Example Text */}
          {showExample && question.example_text && (
            <div className="sm:ml-11 text-sm text-gray-600 bg-gray-50 rounded p-3 border-l-4 border-blue-200">
              <p className="font-medium text-gray-700 mb-1">Example:</p>
              <p>{question.example_text}</p>
            </div>
          )}
        </div>

        {/* Answered Indicator */}
        {isAnswered && (
          <div className="ml-4">
            <svg
              className="w-6 h-6 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Rating Scale */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Your Response:
        </label>

        <div className="flex gap-1 sm:gap-2">
          {Array.from({ length: RATING_SCALE.MAX }, (_, i) => {
            const ratingValue = i + 1;
            const isSelected = value === ratingValue;

            return (
              <button
                key={ratingValue}
                onClick={() => handleRatingChange(ratingValue)}
                className={`flex-1 py-2 px-1 sm:py-3 sm:px-2 rounded-lg border-2 font-medium transition-all ${
                  isSelected
                    ? "border-blue-500 bg-blue-500 text-white shadow-md scale-105"
                    : "border-gray-300 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50"
                }`}
                aria-label={RATING_LABELS[ratingValue]}
                title={RATING_LABELS[ratingValue]}
              >
                <div className="text-lg sm:text-2xl font-bold">
                  {ratingValue}
                </div>
                <div className="text-[10px] sm:text-xs mt-1 leading-tight">
                  {RATING_LABELS[ratingValue].split(" / ")[0]}
                </div>
              </button>
            );
          })}
        </div>

        {/* Rating Scale Labels */}
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <span>Strongly Disagree</span>
          <span>Neutral</span>
          <span>Strongly Agree</span>
        </div>
      </div>

      {/* Optional Context Notes */}
      <div className="mt-4">
        <button
          onClick={() => setShowNotes(!showNotes)}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
        >
          <svg
            className={`w-4 h-4 transition-transform ${showNotes ? "rotate-90" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
          {showNotes ? "Hide" : "Add"} additional context (optional)
        </button>

        {showNotes && (
          <div className="mt-3">
            <textarea
              value={localNotes}
              onChange={(e) => handleNotesChange(e.target.value)}
              placeholder="Add any additional context or notes about your response..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white text-gray-900 placeholder-gray-500"
              rows={3}
            />
          </div>
        )}
      </div>
    </div>
  );
};
